/**
 * Constants and types for file size prefixes
 */
export const DECIMAL_PREFIXES = [
    'B',
    'kB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB',
] as const;

export type DecimalPrefix = (typeof DECIMAL_PREFIXES)[number];

export const BINARY_PREFIXES = [
    'B',
    'KiB',
    'MiB',
    'GiB',
    'TiB',
    'PiB',
    'EiB',
    'ZiB',
    'YiB',
] as const;

export type BinaryPrefix = (typeof BINARY_PREFIXES)[number];

/**
 * Options for formatting file sizes
 */
export type FileSizeFormatOptions = {
    base?: 1000 | 1024;
    binary?: boolean;
    round?: number;
    includePrefix?: boolean;
    minUnit?: DecimalPrefix | BinaryPrefix;
    maxUnit?: DecimalPrefix | BinaryPrefix;
};

/**
 * FileSize class and function implementation
 */
export type FileSizeClass = {
    new (bytes?: number): FileSizeInstance;
    (bytes: number, options?: FileSizeFormatOptions): string;
    parse: (sizeString: string) => FileSizeInstance;
};

export type FileSizeInstance = {
    bytes: number;
    kilobytes: number;
    kibibytes: number;
    megabytes: number;
    mebibytes: number;
    gigabytes: number;
    gibibytes: number;
    format: (options?: FileSizeFormatOptions) => string;
    getAppropriateUnit: (base?: 1000 | 1024) => number;
    add: (other: FileSizeInstance | number) => FileSizeInstance;
    subtract: (other: FileSizeInstance | number) => FileSizeInstance;
    multiply: (factor: number) => FileSizeInstance;
    divide: (divisor: number) => FileSizeInstance;
    equals: (other: FileSizeInstance | number) => boolean;
    greaterThan: (other: FileSizeInstance | number) => boolean;
    lessThan: (other: FileSizeInstance | number) => boolean;
    toString: () => string;
};

// Factory function implementation
function createFileSizeFactory(): FileSizeClass {
    // Constructor implementation
    class FileSizeImpl implements FileSizeInstance {
        private _bytes: number;

        constructor(bytes: number = 0) {
            this._bytes = Math.max(0, bytes);
        }

        get bytes(): number {
            return this._bytes;
        }

        get kilobytes(): number {
            return this._bytes / 1000;
        }

        get kibibytes(): number {
            return this._bytes / 1024;
        }

        get megabytes(): number {
            return this._bytes / 1000 / 1000;
        }

        get mebibytes(): number {
            return this._bytes / 1024 / 1024;
        }

        get gigabytes(): number {
            return this._bytes / 1000 / 1000 / 1000;
        }

        get gibibytes(): number {
            return this._bytes / 1024 / 1024 / 1024;
        }

        format(options: FileSizeFormatOptions = {}): string {
            const {
                base = 1000,
                binary = false,
                round = 2,
                includePrefix = true,
            } = options;

            const prefixList = binary ? BINARY_PREFIXES : DECIMAL_PREFIXES;

            if (this._bytes === 0) {
                return includePrefix ? `0 ${prefixList[0]}` : '0';
            }

            const i = Math.floor(Math.log(this._bytes) / Math.log(base));
            const clampedIndex = Math.min(i, prefixList.length - 1);
            const value = this._bytes / base ** clampedIndex;
            const roundedValue = value.toFixed(round);

            return includePrefix
                ? `${roundedValue} ${prefixList[clampedIndex]}`
                : roundedValue;
        }

        getAppropriateUnit(base: 1000 | 1024 = 1000): number {
            if (this._bytes === 0)
                return 0;
            return Math.min(
                Math.floor(Math.log(this._bytes) / Math.log(base)),
                (base === 1000 ? DECIMAL_PREFIXES : BINARY_PREFIXES).length - 1,
            );
        }

        add(other: FileSizeInstance | number): FileSizeInstance {
            const otherBytes = typeof other === 'number' ? other : other.bytes;
            return new FileSizeImpl(this._bytes + otherBytes);
        }

        subtract(other: FileSizeInstance | number): FileSizeInstance {
            const otherBytes = typeof other === 'number' ? other : other.bytes;
            return new FileSizeImpl(this._bytes - otherBytes);
        }

        multiply(factor: number): FileSizeInstance {
            return new FileSizeImpl(this._bytes * factor);
        }

        divide(divisor: number): FileSizeInstance {
            if (divisor === 0) {
                throw new Error('Cannot divide by zero');
            }
            return new FileSizeImpl(this._bytes / divisor);
        }

        equals(other: FileSizeInstance | number): boolean {
            const otherBytes = typeof other === 'number' ? other : other.bytes;
            return this._bytes === otherBytes;
        }

        greaterThan(other: FileSizeInstance | number): boolean {
            const otherBytes = typeof other === 'number' ? other : other.bytes;
            return this._bytes > otherBytes;
        }

        lessThan(other: FileSizeInstance | number): boolean {
            const otherBytes = typeof other === 'number' ? other : other.bytes;
            return this._bytes < otherBytes;
        }

        toString(): string {
            return this.format();
        }
    }

    // Create the function constructor
    const FileSize = function (
        this: FileSizeInstance | void,
        bytes: number,
        options: FileSizeFormatOptions = {},
    ): string | FileSizeInstance {
        // When called with new, act as a constructor
        if (this instanceof FileSizeImpl) {
            return new FileSizeImpl(bytes);
        }

        // When called as a function, format the bytes and return a string
        return new FileSizeImpl(bytes).format(options);
    } as unknown as FileSizeClass;

    FileSize.parse = (sizeString: string): FileSizeInstance => {
        const regex = /^(\d+(\.\d+)?)\s*([KMGTPEZY]i?B|B)$/i;
        const match = sizeString.trim().match(regex);

        if (!match) {
            throw new Error(`Invalid file size format: ${sizeString}`);
        }

        const value = Number.parseFloat(match[1]);
        const unit = match[3];
        const isBinary = unit.includes('i');
        const base = isBinary ? 1024 : 1000;

        // Get the exponent based on the unit prefix
        let exponent = 0;
        if (unit.toUpperCase() !== 'B') {
            const prefix = unit.charAt(0).toUpperCase();
            const prefixIndex = (
                isBinary ? BINARY_PREFIXES : DECIMAL_PREFIXES
            ).findIndex(p => p.charAt(0).toUpperCase() === prefix);

            if (prefixIndex > 0) {
                exponent = prefixIndex;
            }
        }

        return new FileSizeImpl(value * base ** exponent);
    };

    // Set up the prototype chain
    FileSize.prototype = FileSizeImpl.prototype;

    return FileSize;
}

// Create and export the FileSize function/constructor
export const FileSize = createFileSizeFactory();
